import api from "../../../data/api/mainApi";
import { applyLayout, applyImageLayout } from "./dxfLayoutStorage";
import { detectPartsFromImage } from "./detectImageParts";
import { snapImageFrames } from "./snapImageFrames";
import { validRegions } from "./DxfRegionSelector";
import { validImageRegions } from "./ImageCropper";

export const batchStem = name => name.replace(/\.(dxf|png|jpe?g)$/i, "").normalize("NFD").replace(/\p{M}/gu, "").replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
export function collectBatchFiles(files) {
  const groups = new Map();
  for (const file of files) {
    const dxf = /\.dxf$/i.test(file.name);
    if (!dxf && !/\.(png|jpe?g)$/i.test(file.name)) throw new Error(`Nem támogatott fájl: ${file.name}`);
    if (file.size > (dxf ? 500 : 50) * 1024 * 1024) throw new Error(`Túl nagy fájl: ${file.name}`);
    const key = batchStem(file.name), field = dxf ? "dxfFile" : "imageFile";
    if (!key) throw new Error("Hiányzó fájlnév.");
    const row = groups.get(key) || { id: crypto.randomUUID(), name: file.name.replace(/\.(dxf|png|jpe?g)$/i, "").normalize("NFC").replace(/[^\p{L}\p{N}]/gu, ""),
      included: true, units: 4, dxfRegions: [], imageRegions: [], outputs: null };
    if (row[field]) throw new Error(`Több ${dxf ? "DXF" : "kép"} azonos névvel: ${file.name}. Nevezd át az eltérő forrásokat.`);
    row[field] = file; groups.set(key, row);
  }
  if (groups.size > 150) throw new Error("Legfeljebb 150 forrás használható egy kötegben.");
  return [...groups.values()];
}
export async function withBatchImage(file, action) {
  const url = URL.createObjectURL(file), image = new Image();
  try {
    await new Promise((resolve,reject) => { image.onload = resolve; image.onerror = () => reject(new Error("A kép nem olvasható.")); image.src = url; });
    if (image.naturalWidth * image.naturalHeight > 40000000) throw new Error("Legfeljebb 40 megapixeles kép használható.");
    return await action(image);
  } finally { URL.revokeObjectURL(url); }
}
export async function pollBatchJob(job, onCheckpoint) {
  let current = job;
  while (!["done","error"].includes(current.status)) {
    await new Promise(resolve => setTimeout(resolve, 1500));
    try { current = (await api.get(`/millings/dxf/jobs/${encodeURIComponent(job.id)}`)).data; }
    catch (error) { if (error.response?.status === 404) onCheckpoint?.(null); throw error; }
  }
  onCheckpoint?.(null);
  if (current.status === "error") throw new Error(current.error || "A DXF feldolgozása nem sikerült.");
  return current.result;
}
export async function prepareBatchRow(row, checkpoint) {
  let next = { ...row, error: "" };
  if (next.imageFile && !next.size) {
    next.size = await withBatchImage(next.imageFile, image => ({ w: image.naturalWidth, h: image.naturalHeight }));
    checkpoint(next);
  }
  if (next.dxfFile && !next.scene) {
    if (!next.dxfKey) {
      const body = new FormData(); body.append("file", next.dxfFile);
      next.dxfKey = (await api.post("/millings/dxf", body)).data.dxfKey; checkpoint({ ...next });
    }
    if (!next.inspectJob) {
      next.inspectJob = (await api.post(`/millings/dxf/${next.dxfKey}/inspect`)).data; checkpoint({ ...next });
    }
    const result = await pollBatchJob(next.inspectJob, () => { next.inspectJob = null; checkpoint({ ...next }); });
    if (!result?.scene?.lines?.length) throw new Error("Nincs megjeleníthető DXF-geometria.");
    next.scene = result.scene;
    next.units = [1,4,5,6].includes(result.units) ? result.units : 4;
    next.warning = ![1,4,5,6].includes(result.units) ? "Ismeretlen DXF mértékegység; ellenőrizd a milliméter beállítást." : "";
  }
  return next;
}
export async function applyBatchTemplate(row, layout, options) {
  let next = { ...row, outputs: null, splitJob: null, layoutName: layout.name, error: "" };
  const prefix = regions => regions.map(r => ({ ...r, name: `${row.name}-${r.name}` }));
  if (row.dxfFile) {
    if (!row.scene) throw new Error("Előbb elemezd a DXF-et.");
    next.dxfRegions = prefix(applyLayout(layout, row.units, row.scene, options.align));
  }
  if (row.imageFile) await withBatchImage(row.imageFile, image => {
    next.size = { w: image.naturalWidth, h: image.naturalHeight };
    let target = row.target || [0,0,next.size.w,next.size.h];
    if (options.detect && layout.sourceType !== "image") {
      const rects = detectPartsFromImage(image,28).rects;
      if (rects.length) target = [Math.min(...rects.map(r=>r[0])),Math.min(...rects.map(r=>r[1])),Math.max(...rects.map(r=>r[2])),Math.max(...rects.map(r=>r[3]))];
    }
    next.target = target;
    const regions = prefix(applyImageLayout(layout,next.size,target));
    next.imageRegions = options.snap ? snapImageFrames(image,regions,20) : regions;
  });
  return next;
}
export function batchRowValid(row) {
  if (row.inspectJob) return false;
  if (row.dxfFile && !validRegions(row.dxfRegions)) return false;
  if (row.imageFile && !validImageRegions(row.imageRegions,row.size)) return false;
  const names = regions => regions.map(r=>r.name).sort().join("\n");
  return (!row.dxfFile || !row.imageFile || names(row.dxfRegions) === names(row.imageRegions));
}
export async function splitBatchRow(row, checkpoint) {
  if (!batchRowValid(row)) throw new Error("Ellenőrizd a kereteket és a kép/DXF részeinek egyező neveit.");
  if (row.outputs) return row;
  let next = { ...row, error: "" };
  if (row.dxfFile) {
    if (!next.splitJob) {
      next.splitJob = (await api.post(`/millings/dxf/${row.dxfKey}/split`, { units: row.units,
        blocks: row.dxfRegions.map(r=>({block:"*MODEL_SPACE",name:r.name,region:JSON.stringify(r.rect),excludeRegions:r.cutouts.map(c=>JSON.stringify(c.rect))})) })).data;
      checkpoint({ ...next });
    }
    const result = await pollBatchJob(next.splitJob, () => { next.splitJob = null; checkpoint({ ...next }); });
    if (!result?.files?.length) throw new Error("A szerver nem adott vissza darabolt DXF-et.");
    next.outputs = result.files;
    next.warning = result.files.some(f=>f.excludedCrossing>0) ? "Határon átnyúló DXF-elemek kimaradtak. Ellenőrizd és szükség esetén bővítsd a kereteket." : next.warning;
  } else next.outputs = [];
  return next;
}
export async function cropBatchImage(image, region) {
  const [x0,y0,x1,y1] = region.rect.map(Math.round);
  const canvas = document.createElement("canvas"); canvas.width=x1-x0; canvas.height=y1-y0;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("A kép darabolása nem indítható.");
  context.drawImage(image,x0,y0,x1-x0,y1-y0,0,0,x1-x0,y1-y0);
  for (const cutout of region.cutouts) {
    const [a,b,c,d]=cutout.rect.map(Math.round); context.clearRect(a-x0,b-y0,c-a,d-b);
  }
  return new Promise((resolve,reject)=>canvas.toBlob(blob=>blob ? resolve(blob) : reject(new Error("A kép mentése sikertelen.")),"image/png"));
}
